import { Button } from 'react-bootstrap'
import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useStateValue } from '@State'
import { client } from '@Utils'
import { canvasConnect as endpoints } from '@URLS'
import { toast } from '@CommonComponents/toast'
import { LoadingWave } from '../../components'

const EditableCourseName = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  const startEdit = () => {
    setDraft(value)
    setEditing(true)
  }

  const saveEdit = () => {
    const trimmed = draft.trim()
    if (!trimmed) {
      toast.error('Course name cannot be empty.')
      return
    }
    onSave(trimmed)
    setEditing(false)
  }

  const cancelEdit = () => {
    setDraft(value)
    setEditing(false)
  }

  if (editing) {
    return (
      <div>
        <label htmlFor="new-course-title" className="d-block">
          Name of resource to be created:
        </label>
        <input
          id="new-course-title"
          type="text"
          className="form-control"
          maxLength={250}
          value={draft}
          autoFocus
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') saveEdit()
            else if (e.key === 'Escape') cancelEdit()
          }}
        />
        <div className="mt-2">
          <button
            type="button"
            className="btn btn-sm btn-myed btn-primary btn-primary-myed mr-2"
            onClick={saveEdit}
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn-sm btn-myed btn-default btn-default-myed"
            onClick={cancelEdit}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="text-muted small">Name of resource to be created:</div>
      <div className="d-flex align-items-start">
        <p className="my-2 mr-2" style={{ wordBreak: 'break-word' }}>
          {value ? (
            <strong>{value}</strong>
          ) : (
            <span className="text-muted small font-italic">
              No course name set
            </span>
          )}
        </p>
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          title="Edit course name"
          aria-label="Edit course name"
          onClick={startEdit}
        >
          <i className="fa-regular fa-pencil" />
        </button>
      </div>
    </div>
  )
}

EditableCourseName.propTypes = {
  value: PropTypes.string,
  onSave: PropTypes.func.isRequired,
}

const ConnectResourceComp = props => {
  const [currentState, dispatch] = useStateValue()
  const [loading, setLoading] = useState(false)
  const [courseTitle, setCourseTitle] = useState('')
  const [titleEdited, setTitleEdited] = useState(false)
  let contextId = window.location.pathname.split('/')[4]
  useEffect(() => {
    async function getConnectResourceData(contextId, dispatch) {
      try {
        let resp = await client.get(endpoints.getInstructorData({ contextId }))
        await dispatch({
          type: 'updateInstructorData',
          payload: {
            ...resp.data,
          },
        })
      } catch (err) {
        console.log(err)
        toast.error(err.response.data.error)
      }
    }
    getConnectResourceData(contextId, dispatch)
  }, [])

  // Prepopulate the course-name textbox with the Canvas context title once it
  // arrives, but don't clobber edits the instructor has already typed. We treat
  // `undefined` as "not yet loaded" so an empty contextTitle from the backend
  // still ends the loading state instead of silently leaving the field blank.
  useEffect(() => {
    if (!titleEdited && currentState.contextTitle !== undefined) {
      setCourseTitle(currentState.contextTitle)
    }
  }, [currentState.contextTitle, titleEdited])

  let resetApp = () => {
    dispatch({
      type: 'reset',
    })
    props.history.goBack()
  }

  const createConnection = async (cnumToCopy, history) => {
    setLoading(true)
    try {
      setLoading(true)
      await client.post(endpoints.createNewCourse(), {
        source_coursenum: cnumToCopy,
        contextId: contextId,
        course_title: courseTitle.trim(),
      })
      setLoading(false)
      history.push(
        `/links/canvas_connect.instructor_setup/-/${contextId}${window.location.search}`
      )
      toast.success('Your resource has been successfully linked.')
    } catch (err) {
      setLoading(false)
      toast.error(err.response.data.error, { autoClose: false })
    }
  }

  if (loading) {
    return (
      <React.Fragment>
        <h2>Copying and Connecting Selected Resource</h2>
        <LoadingWave />
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      {currentState.connectedCourses.length === 0 && (
        <div className="ml-5">
          <div className="ml-5">
            <h2>Welcome, {currentState.instrFirstName}</h2>
            <h4>Select a MyEducator resource to copy and connect to Canvas.</h4>
          </div>
        </div>
      )}
      <div className="ml-5 mt-4 d-flex justify-content-between">
        {currentState.instrCourses.length > 0 ? (
          <div className="ml-5">
            <select
              onChange={event =>
                dispatch({
                  type: 'coursenumToCopy',
                  coursenumToCopy: event.target.value,
                })
              }
            >
              <option value="">Select an available resource</option>
              {currentState.instrCourses.map((childCourse, i) => (
                <option key={i} value={childCourse.coursenum}>
                  {`${childCourse.course_title} Created: ${childCourse.create_date}`}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="ml-5">
            <p>
              The email address <i>{currentState.instrEmail}</i> does not belong
              to any MyEducator resources.
            </p>
            <p>
              Please contact your MyEducator representative or email us at{' '}
              <a href="mailto:support@myeducator.com">support@myeducator.com</a>{' '}
              for assistance.
            </p>
          </div>
        )}
        {currentState.connectedCourses.length > 0 && (
          <div className="btn btn-link text-dec-none" onClick={resetApp}>
            <div className="fa-solid fa-times fa-fw" /> Cancel
          </div>
        )}
      </div>
      {currentState.instrCourses.length > 0 && (
        <div className="ml-5 mt-4">
          <div className="ml-5" style={{ maxWidth: '600px' }}>
            <EditableCourseName
              value={courseTitle}
              onSave={newTitle => {
                setTitleEdited(true)
                setCourseTitle(newTitle)
              }}
            />
            <small className="form-text text-muted mt-2">
              Defaults to the Canvas course name. Click the pencil to rename
              this resource so it&apos;s easier to identify in MyEducator (especially
              when connecting more than one resource to the same Canvas course).
            </small>
          </div>
        </div>
      )}
      <div className="ml-5">
        <Button
          disabled={
            currentState.coursenumToCopy === '' || courseTitle.trim() === ''
          }
          className="btn btn-success btn-myed btn-success-myed ml-5 mt-4"
          onClick={() =>
            createConnection(currentState.coursenumToCopy, props.history)
          }
        >
          Create Connection
        </Button>
      </div>
    </React.Fragment>
  )
}

ConnectResourceComp.propTypes = {
  history: PropTypes.object.isRequired,
}

export const ConnectResource = withRouter(ConnectResourceComp)
